import { Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import {
    CircularProgress, Tooltip, Select, MenuItem, FormControl, FormLabel,
    InputAdornment, TextField, Button, TableContainer, Table, TableHead, TableBody, Paper,
    TablePagination, Backdrop, Dialog, DialogContent, DialogTitle, Box, InputLabel
} from '@mui/material';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { ContextSession } from "../../../services/ContextSession";
import FilterListIcon from '@mui/icons-material/FilterList';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import { useMediaQuery } from '@mui/material';

async function getSolicitudes(ip, accesos) {
    const url = `https://be.injupemp.gob.hn:8443/api/quejasSugerencia/Consultar/`;
    const auto = "Bearer " + accesos;

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            "Authorization": auto
        }
    });

    if (response.ok) {
        return response.json();
    } else {
        throw new Error('Error en la solicitud');
    }
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#19438C',
        color: 'white',
        fontSize: 16,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        borderRight: 'none',
        borderLeft: 'none',
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
    '&:hover': {
        backgroundColor: '#C1C1C1',
    },
}));

function ListaQuejasSugerencias() {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    let sessi = useContext(ContextSession);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(''); // Filtro de estado
    const [filas, setFilas] = useState([]);

    useEffect(() => {
        async function fetchData() {
            try {
                const data = await getSolicitudes(sessi.ip, sessi.sessionData.token);

                // Ordenar primero por Estado y luego por ID
                const sortedData = data.sort((a, b) => {
                    if (a.estado === "Pendiente" && b.estado !== "Pendiente") {
                        return -1;
                    } else if (a.estado !== "Pendiente" && b.estado === "Pendiente") {
                        return 1;
                    }
                    return a.ID - b.ID;
                });

                setFilas(sortedData);
                setLoading(false);
            } catch (error) {
                console.error('Error al obtener datos:', error);
                setLoading(false);
            }
        }
        fetchData();
    }, [sessi.ip]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const navigate = useNavigate();

    const handleSearchChange = (event) => {
        const searchTerm = event.target.value.toLowerCase();
        setSearchTerm(searchTerm);
    };

    const handleStatusChange = (event) => {
        setStatusFilter(event.target.value);
        setPage(0); // Reiniciar la página a la 0 cuando se cambie el filtro
    };

    // Filtrar las filas según el estado y el término de búsqueda
    const filteredRows = filas.filter((row) => {
        return (
            (statusFilter === '' || row.estado.toLowerCase().includes(statusFilter.toLowerCase())) &&
            (
                searchTerm === '' ||
                (searchTerm && (
                    row.numerocorrelativo?.toString().includes(searchTerm) ||
                    row.descripcion?.toLowerCase().includes(searchTerm)
                ))
            )
        );
    });

    const getBackgroundColor = (diasDesdeRegistro) => {
        if (diasDesdeRegistro < 10) {
            return '#C6F7D0'; // verde claro
        } else if (diasDesdeRegistro === 10) {
            return '#F7DC6F'; // amarillo claro
        } else {
            return '#FE0000'; // rojo claro
        }
    };

    const handleClickCarga = (id, fecha) => {
        sessi.setVariable("IDquejasugerencia", id);
        sessi.setVariable("fecharegistro", fecha);
        navigate(`/PortalDelParticipante/VerQuejasSugerencias`);
    };

    return (
        <div className="container" style={{ marginTop: 10 }}>
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                    <CircularProgress />
                </div>
            ) : (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <h2><b>Lista de Quejas y Sugerencias</b></h2>
                    </div>

                    {/* Filtro de estado */}
                    <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
                        <FormControl style={{ minWidth: 200 }}>
                            <InputLabel>Filtrar por estado</InputLabel>
                            <Select
                                value={statusFilter}
                                onChange={handleStatusChange} // Actualiza el estado del filtro
                                label="Filtrar por estado"
                            >
                                <MenuItem value="">Todas</MenuItem>
                                <MenuItem value="Cerrado">Cerrado</MenuItem>
                                <MenuItem value="Pendiente">Pendiente</MenuItem>
                            </Select>
                        </FormControl>
                    </div>

                    <div className="row" style={{ marginTop: '20px' }}>
                        <TableContainer component={Paper}>
                            <Table style={{ fontSize: isMobile ? '7px' : '14px' }}>
                                <TableHead>
                                    <StyledTableRow>
                                        <StyledTableCell style={{ textAlign: 'center' }}><b>#</b></StyledTableCell>
                                        <StyledTableCell style={{ textAlign: 'center' }}><b>No. Solicitud</b></StyledTableCell>
                                        <StyledTableCell style={{ textAlign: 'center' }}><b>Nombre</b></StyledTableCell>
                                        <StyledTableCell style={{ textAlign: 'center' }}><b>Tipo De Solicitud</b></StyledTableCell>
                                        <StyledTableCell style={{ textAlign: 'center' }}><b>Fecha Registro</b></StyledTableCell>
                                        <StyledTableCell style={{ textAlign: 'center' }}><b>Días Para Revisión</b></StyledTableCell>
                                        <StyledTableCell style={{ textAlign: 'center' }}><b>Estado</b></StyledTableCell>
                                        <StyledTableCell style={{ textAlign: 'center' }}><b>Acciones</b></StyledTableCell>
                                    </StyledTableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredRows.length > 0 ? (
                                        (rowsPerPage > 0
                                            ? filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            : filteredRows
                                        ).map((row, index) => (
                                            <StyledTableRow key={row.ID}>
                                                <StyledTableCell style={{ textAlign: 'center' }}>{row.ID}</StyledTableCell>
                                                <StyledTableCell style={{ textAlign: 'center' }}>{row.numerocorrelativo}</StyledTableCell>
                                                <StyledTableCell style={{ textAlign: 'center' }}>{row.nombre_participante}</StyledTableCell>
                                                <StyledTableCell style={{ textAlign: 'center' }}>
                                                    {row.tipo === 1 ? 'Queja' : 'Sugerencia'}
                                                </StyledTableCell>
                                                <StyledTableCell style={{ textAlign: 'center' }}>
                                                    {new Date(row.fecharegistro).toLocaleDateString()}
                                                </StyledTableCell>
                                                <StyledTableCell style={{ textAlign: 'center' }}>
                                                    <span
                                                        style={{
                                                            backgroundColor: getBackgroundColor(row.dias_desde_registro),
                                                            borderRadius: 10,
                                                            padding: 10,
                                                        }}
                                                    >
                                                        {row.dias_desde_registro}/10
                                                    </span>
                                                </StyledTableCell>
                                                <StyledTableCell style={{ textAlign: 'center' }}>{row.estado}</StyledTableCell>
                                                <StyledTableCell style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                    <div className="action-buttons" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                        <Link onClick={() => handleClickCarga(row.ID, row.fecharegistro)} title="Ver" style={{ color: '#10ab80', margin: '0 1px' }}>
                                                            <i className="material-icons">&#xE417;</i>
                                                        </Link>
                                                    </div>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))
                                    ) : (
                                        <StyledTableRow>
                                            <StyledTableCell colSpan={8} style={{ textAlign: 'center' }}>
                                                No se encontraron datos
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {filteredRows.length > 0 && (
                            <TablePagination
                                rowsPerPageOptions={isMobile ? [3, 5, 10] : [5, 10, 20]}
                                component="div"
                                count={filteredRows.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default ListaQuejasSugerencias;
